import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Logo } from "@/components/Logo";
import { Lock, ArrowRight, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Gate({ onUnlock }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!password.trim()) return;
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/verify`, { password });
      if (data.authenticated) {
        sessionStorage.setItem("utopia_token", data.token);
        onUnlock();
      }
    } catch (err) {
      setError("Invalid access code. Please try again.");
      setPassword("");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center relative overflow-hidden bg-navy px-6">
      {/* ambient glow */}
      <div className="absolute -top-40 -right-40 h-[500px] w-[500px] rounded-full bg-flame/10 blur-[80px]" />
      <div className="absolute -bottom-52 -left-32 h-[460px] w-[460px] rounded-full bg-flame/5 blur-[80px]" />
      <div className="grain" />

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        className="relative z-10 w-full max-w-md"
      >
        <div className="mb-10 flex flex-col items-center text-center">
          <Logo size={46} />
          <div className="mt-10 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1.5">
            <Lock className="h-3.5 w-3.5 text-flame" />
            <span className="font-mono text-[0.7rem] tracking-[0.2em] uppercase text-white/60">
              Confidential Access
            </span>
          </div>
          <h1 className="mt-8 font-display font-extrabold text-3xl sm:text-4xl tracking-tight text-white">
            Investment Portal
          </h1>
          <p className="mt-3 font-mono text-xs sm:text-sm tracking-wide text-flame/90">
            Accredited Investors Only, Seed Round
          </p>
        </div>

        <form onSubmit={submit} data-testid="gate-form" className="space-y-4">
          <div className="relative">
            <input
              data-testid="gate-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter access code"
              autoFocus
              className="w-full rounded-xl border border-white/15 bg-white/[0.03] px-5 py-4 text-white placeholder:text-white/30 outline-none transition-all duration-300 focus:border-flame/60 focus:bg-white/[0.05] font-body"
            />
          </div>

          {error && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              data-testid="gate-error"
              className="text-sm text-red-400 font-mono"
            >
              {error}
            </motion.p>
          )}

          <button
            data-testid="gate-submit-button"
            type="submit"
            disabled={loading}
            className="group flex w-full items-center justify-center gap-2 rounded-xl bg-flame px-6 py-4 font-display font-bold text-navy transition-all duration-300 hover:bg-ember disabled:opacity-60"
          >
            {loading ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <>
                Enter
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </>
            )}
          </button>
        </form>

        <p className="mt-10 text-center font-mono text-[0.65rem] tracking-[0.2em] uppercase text-white/25">
          U-TOPIA, Inc.
        </p>
      </motion.div>
    </div>
  );
}
